/*
* Values & functions for handling dates and times
* @ASSUMPTION these won't be used in long-lived/long-running scripts, as things like NOW are declared as constants and become incorrect with time
*/

//
// Labels used in this file:
//
// @ASSUMPTION
// @DEBUG
// @NB
// @OPTIMISE
// @REFACTOR
// @TEMP
// @TODO
//

// @ts-check

import {zeroPad} from "./utils";

export const isDate = o => o instanceof Date;
export const castToDateIfNotDate = d => isDate(d) ? d : new Date(d);
export const toDate = d => castToDateIfNotDate(d);
export const dateToMilliseconds = d => d.valueOf();

// Useful Constants

export const MILLISECONDS_IN_A_SECOND = 1000;
export const SECONDS_IN_A_MINUTE = 60;
export const MINUTES_IN_AN_HOUR = 60;
export const HOURS_IN_A_DAY = 24;
export const DAYS_IN_A_WEEK = 7;
export const DAYS_IN_A_YEAR = 365;  // @TODO really?
export const MILLISECONDS_IN_A_MINUTE = MILLISECONDS_IN_A_SECOND * SECONDS_IN_A_MINUTE;
export const MILLISECONDS_IN_AN_HOUR = MILLISECONDS_IN_A_MINUTE * MINUTES_IN_AN_HOUR;
export const MILLISECONDS_IN_A_DAY = MILLISECONDS_IN_AN_HOUR * HOURS_IN_A_DAY;

export const DAYS_OF_WEEK = [
    'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'
];
export const MONTHS_OF_YEAR = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];


// Useful Date Objects


// export const NOW = new Date();
export const now = () => new Date();
export const isoDateNow = () => now().toISOString();
export const previousMidnight = () => new Date().setHours(0, 0, 1, 0);
export const yesterdayMidnight = () => new Date().setTime(dateToMilliseconds(previousMidnight()) - MILLISECONDS_IN_A_DAY);
export const nextMidnight = () => new Date().setHours(23, 59, 59, 0);
export const tomorrowMidnight = () => new Date().setTime(dateToMilliseconds(nextMidnight()) + MILLISECONDS_IN_A_DAY);

export const getDateOffsetByMSFromNow = ms => new Date(dateToMilliseconds(now()) + ms);
export const ahead = n => n;
export const ago = n => n * -1;
export const hrsAgo = n => getDateOffsetByMSFromNow(ago(n * MILLISECONDS_IN_AN_HOUR));
export const hrsAhead = n => getDateOffsetByMSFromNow(ahead(n * MILLISECONDS_IN_AN_HOUR));

export const TWENTY_FOUR_HOURS_AGO = hrsAgo(24);
export const FORTY_EIGHT_HOURS_AGO = hrsAgo(48);
export const TWENTY_FOUR_HOURS_AHEAD = hrsAhead(24);
export const FORTY_EIGHT_HOURS_AHEAD = hrsAhead(48);

export const ONE_YEAR_AHEAD = getDateOffsetByMSFromNow(MILLISECONDS_IN_A_DAY * DAYS_IN_A_YEAR);


export const DAY_OF_YEAR_INDEX = Math.floor(
    (() => {
        const NOW = now();
        const start = new Date(NOW.getFullYear(), 0, 0);
        // @ts-ignore
        return (NOW - start) + ((start.getTimezoneOffset() - NOW.getTimezoneOffset()) * MILLISECONDS_IN_A_MINUTE);
   })()
   / MILLISECONDS_IN_A_DAY
) - 1;


// ISODates

// Indeces of key things in an ISODate, e.g. 2019-10-13T09:00:21.886Z
export const INDEX_OF_DAY_OF_MONTH_IN_ISO_DATE = 8;
export const INDEX_END_OF_DAY_OF_MONTH_IN_ISO_DATE = 10;

/**
 * Return a Date as a String in 'YYYY-MM-DD' format
 * @param {Date} d
 * @returns {string}
 */
export const yyyymmdd = (d) => {
    return d.getFullYear() + '-' + zeroPad(d.getMonth() + 1) + '-' + zeroPad(d.getDate());
};

export const yyyymmddhhmmss = (d) => {
    return `${yyyymmdd((d))}-${zeroPad(d.getHours())}-${zeroPad(d.getMinutes())}-${zeroPad(d.getSeconds())}`;
};

export const isoTime = d => {
    return `${zeroPad(d.getHours())}:${zeroPad(d.getMinutes())}`;
};

export const getTimeDiffAsDaysHoursMinsText = ms_diff_from_now => {
    let breakdown = {}, str = [];

    let abs_diff = Math.abs(ms_diff_from_now);

    breakdown.days = Math.floor(abs_diff / MILLISECONDS_IN_A_DAY);
    abs_diff = abs_diff % MILLISECONDS_IN_A_DAY;
    breakdown.hours = Math.floor(abs_diff / (MILLISECONDS_IN_A_SECOND * SECONDS_IN_A_MINUTE * MINUTES_IN_AN_HOUR));
    abs_diff = abs_diff % (MILLISECONDS_IN_A_SECOND * SECONDS_IN_A_MINUTE * MINUTES_IN_AN_HOUR);
    breakdown.mins = Math.floor(abs_diff / (MILLISECONDS_IN_A_SECOND * SECONDS_IN_A_MINUTE));

    if(breakdown.days > 0) {
        str.push(`${breakdown.days}d`);
    }
    if(breakdown.hours > 0) {
        str.push(`${breakdown.hours}h`);
    }
    if(breakdown.mins > 0) {
        str.push(`${breakdown.mins}m`);
    }
    else {
        str.push(`seconds`);
    }

    if(ms_diff_from_now < 0) {
        str.unshift(`in`);
    }
    else {
        str.push(`ago`);
    }

    return str.join(' ');
};


export const getTimeDiffAsShortText = ms_diff_from_now => {
    const MS_IN_A_MINUTE = MILLISECONDS_IN_A_SECOND * SECONDS_IN_A_MINUTE;
    const MS_IN_AN_HOUR = MS_IN_A_MINUTE * MINUTES_IN_AN_HOUR;
    const ms_previous_midnight = dateToMilliseconds(previousMidnight());
    const ms_until_previous_midnight = dateToMilliseconds(now()) - ms_previous_midnight;

    // Today
    if(ms_diff_from_now <= ms_until_previous_midnight) {
        let mins = ms_diff_from_now / MS_IN_A_MINUTE;
        if(mins < 1) {
            return `seconds ago`;
        }
        if(mins < MINUTES_IN_AN_HOUR) {
            return `${Math.ceil(mins)} mins ago`;
        }
        return `${Math.ceil(mins/MINUTES_IN_AN_HOUR)} hrs ago`;
    }

    // Yesterday
    let ms_diff_from_previous_midnight = ms_diff_from_now - ms_until_previous_midnight;
    const hrs_ago = ms_diff_from_previous_midnight / MS_IN_AN_HOUR;
    if(hrs_ago <= HOURS_IN_A_DAY) {
        return 'yesterday';
    }

    // Up to 14 days ago
    const NUM_DAYS_BEFORE_DOING_DATES = 14;
    if(hrs_ago <= HOURS_IN_A_DAY*NUM_DAYS_BEFORE_DOING_DATES) {
        return `${Math.ceil(hrs_ago/HOURS_IN_A_DAY)} days ago`
    }

    // Within the last year
    const d = toDate(dateToMilliseconds(now()) - ms_diff_from_now);
    if(hrs_ago <= 14*DAYS_IN_A_YEAR) {
        return `${d.getDate()} ${MONTHS_OF_YEAR[d.getMonth()]}`;
    }

    // More than a year
    return `${d.getDate()} ${MONTHS_OF_YEAR[d.getMonth()]} ${d.getFullYear()}`;
};


export const getTimeDiffAsDaysAgo = ms_diff_from_now => {
    const MS_IN_A_MINUTE = MILLISECONDS_IN_A_SECOND * SECONDS_IN_A_MINUTE;
    const MS_IN_AN_HOUR = MS_IN_A_MINUTE * MINUTES_IN_AN_HOUR;
    const ms_previous_midnight = dateToMilliseconds(previousMidnight());
    const ms_until_previous_midnight = dateToMilliseconds(now()) - ms_previous_midnight;

    // Today
    if(ms_diff_from_now <= ms_until_previous_midnight) {
        return `today`;
    }

    // Yesterday
    let ms_diff_from_previous_midnight = ms_diff_from_now - ms_until_previous_midnight;
    const hrs_ago = ms_diff_from_previous_midnight / MS_IN_AN_HOUR;
    if(hrs_ago <= HOURS_IN_A_DAY) {
        return 'yesterday';
    }

    // Up to 14 days ago
    const NUM_DAYS_BEFORE_DOING_DATES = 14;
    if(hrs_ago <= HOURS_IN_A_DAY*NUM_DAYS_BEFORE_DOING_DATES) {
        return `${Math.ceil(hrs_ago/HOURS_IN_A_DAY)} days ago`
    }

    // Within the last year
    const d = toDate(dateToMilliseconds(now()) - ms_diff_from_now);
    if(hrs_ago <= 14*DAYS_IN_A_YEAR) {
        return `${d.getDate()} ${MONTHS_OF_YEAR[d.getMonth()]}`;
    }

    // More than a year
    return `${d.getDate()} ${MONTHS_OF_YEAR[d.getMonth()]} ${d.getFullYear()}`;
};


// Formatting Functions

export const get12HrTimeStrForDate = d => {
    const date_obj = castToDateIfNotDate(d);
    let ampm = 'am';
    let hour = date_obj.getHours();
    if(hour >= 12) {
        ampm = 'pm';
        if(hour > 12) {
            hour = hour - 12;
        }
    }
    let mins = date_obj.getMinutes();
    if(mins < 10) {
        mins = '0' + mins;
    }
    return `${hour}:${mins}${ampm}`;
};

export const getOrdinalIndicatorStrForDate = d => {
    const date_obj = castToDateIfNotDate(d);
    const n = date_obj.getDate();
    if(n === 11 || n === 12 || n === 13) return 'th';
    if(n % 10 === 1) return 'st';
    if(n % 10 === 2) return 'nd';
    if(n % 10 === 3) return 'rd';
    return 'th';
};

export const getDayDateMonthStrForDate = d => {
    const date_obj = castToDateIfNotDate(d);
    return `${DAYS_OF_WEEK[date_obj.getDay()]} ${date_obj.getDate()}${getOrdinalIndicatorStrForDate(date_obj)} ${MONTHS_OF_YEAR[date_obj.getMonth()]}`;
};
